import * as Sentry from "@sentry/nextjs"
import { Button, Label, Radio, Spinner, TextInput } from "flowbite-react"
import { Form, Formik, FormikProps } from "formik"
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import * as Yup from "yup"

import FormGroup from "@components/elements/FormGroup"
import MonoSelect from "@components/elements/Select"
import useAuth from "@hooks/useAuth"
import { useMediaQuery } from "@hooks/useMediaQuery"
import useToast from "@hooks/useToast"
import MembershipIcon from "@icons/MemberShipIcon"
import ValidPromoCodeIcon from "@icons/ValidPromoCode"
import CustomerOtpVerificationModal from "@modules/auth/pages/CustomerLoginPage/CustomerOtpVerificationModal"
import * as CustomerServices from "@mono/essentials/services/CustomerService"
import { SendOtpFieldType, SendOtpResponseType } from "@mono/essentials/types/CustomerTypes"
import { SelectOptionType } from "@mono/essentials/types/GlobalConfigTypes"

interface verifyMembershipFormProps {
  isIntegratedPage: boolean
  onSuccessVerification?: () => void
  onMemberShipSelect?: (
    newValue: string,
    action: string,
    callback?: (valid?: boolean) => void
  ) => void
  options?: SelectOptionType[]
  selectedMembershipId?: string
  showMembershipDropdown?: boolean
  showCustomerLogin?: boolean
  autoApplyMembership?: boolean
}

const VerifyMembershipForm = (
  {
    isIntegratedPage,
    onSuccessVerification,
    onMemberShipSelect,
    options,
    selectedMembershipId = "",
    showMembershipDropdown = true,
    showCustomerLogin = true,
    autoApplyMembership = false,
  }: verifyMembershipFormProps,
  ref: React.Ref<unknown> | undefined
) => {
  const [isRequestingOtp, setIsRequestingOtp] = useState<boolean>(false)
  const { loggedInCustomerDetails } = useAuth()
  const [showOtpVerificationModal, setShowOtpVerificationModal] =
    useState<SendOtpResponseType>(undefined)
  const { showToast } = useToast()
  const { isCustomerLoggedIn, publicDomainDetails } = useAuth()
  const formikRef = useRef<FormikProps<any>>(null)
  const isMobile = useMediaQuery(425)

  const validationSchema = Yup.object().shape({
    field_type: Yup.string(),
    email: Yup.string()
      .nullable()
      .when("field_type", (field_type, schema) => {
        return field_type[0] === "specificValue"
          ? schema
              .email("Please enter a valid email")
              .matches(/^[^*/]*$/, "Invalid Email")
              .test(
                "is-lowercase-and-no-slash",
                "*Email Required",
                (value) => !value?.match(/[A-Z]/)
              )
              .required("*Please enter your email")
          : schema.nullable()
      }),
  })

  const verifyMembershipCallback = (valid) => {
    if (!valid) {
      formikRef.current?.setFieldValue("membership_id", "")
    }
  }

  useEffect(() => {
    if (autoApplyMembership && !selectedMembershipId?.length && options?.length) {
      formikRef.current?.setFieldValue("membership_id", options[0])
      onMemberShipSelect?.(options[0].value, "apply", verifyMembershipCallback)
    }
  }, [options?.length])

  const intialValues = useMemo(() => {
    return {
      field_type: "email",
      memeber_or_customer_id: "",
      email: loggedInCustomerDetails?.email ?? "",
      membership_id: selectedMembershipId?.length ? selectedMembershipId : "",
    }
  }, [loggedInCustomerDetails, selectedMembershipId])

  const onSubmit = async (values) => {
    setIsRequestingOtp(true)
    try {
      const { data: sendOtpResponse } = await CustomerServices.sendOtp(
        {
          field_type: SendOtpFieldType.EMAIL,
          value: values.email,
          type: "Customer",
        },
        isIntegratedPage
      )
      showToast({
        heading: sendOtpResponse.message,
      })
      if (sendOtpResponse) {
        setShowOtpVerificationModal(sendOtpResponse)
      }
    } catch (e) {
      Sentry.captureException(e)
      showToast({
        heading:
          e?.response?.data?.message ??
          e?.message ??
          "Unable to send the OTP! please try again later.",
        type: "danger",
      })
    } finally {
      setIsRequestingOtp(false)
    }
  }

  const onSuccessOtpVerification = () => {
    setShowOtpVerificationModal(undefined)
    onSuccessVerification?.()
  }

  useImperativeHandle(ref, () => ({
    OnClearMemberValue: () => {
      formikRef?.current?.setFieldValue("membership_id", "")
    },
  }))

  return (
    <>
      <div
        className={`flex items-center bg-primary-100 border border-neutral-450 rounded-md mb-6 py-4 gap-3 ${isMobile ? "px-4" : "px-6"}`}
      >
        <div className={"w-full"}>
          <Formik
            className={"h-full"}
            innerRef={formikRef}
            initialValues={intialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, getFieldProps, submitForm }) => (
              <Form className={"h-full"} autoComplete={"off"}>
                <FormGroup label={""} name={"field_type"}>
                  <div className={"flex items-center gap-3"}>
                    {/* {MEMBERSHIP_VERIFICATION_OPTIONS.map((option) => (
                          <div
                            className="flex items-center gap-3"
                            key={option.value}
                          >
                            <Radio
                              id={option.value}
                              name="field_type"
                              value={option.value}
                              checked={Boolean(
                                values.field_type &&
                                  values.field_type === option.value
                              )}
                              onChange={() =>
                                setFieldValue("field_type", option.value)
                              }
                            />
                            <Label
                              htmlFor={option.value}
                              className="text12Medium text-neutral-900"
                            >
                              {option.label}
                            </Label>
                          </div>
                        ))} */}
                  </div>
                </FormGroup>
                {showCustomerLogin ? (
                  <div className={`${isMobile ? "flex-col" : "flex-row items-center"} flex gap-3`}>
                    {values?.field_type === "email" ? (
                      <div className={"flex items-center gap-3 w-full"}>
                        {/* icon */}
                        <div className={"p-1 rounded-md bg-white border-2 border-primary-300"}>
                          <MembershipIcon className={"text-primary-600 h-5 w-5 "} />
                        </div>

                        <FormGroup label={""} name={"email"} className={"w-full"}>
                          <TextInput
                            {...getFieldProps("email")}
                            sizing={"md"}
                            type={"email"}
                            autoComplete={"off"}
                            disabled={isCustomerLoggedIn}
                            className={"bg-whiteBlack-100 rounded-md w-full"}
                            placeholder={"Enter Email"}
                            color={errors.email && touched.email ? "failure" : undefined}
                          />
                        </FormGroup>
                      </div>
                    ) : null}

                    {values.field_type === "member_code" ? (
                      <FormGroup label={""} name={"memeber_or_customer_id"} className={"w-full"}>
                        <TextInput
                          id={"memeber_or_customer_id"}
                          {...getFieldProps("memeber_or_customer_id")}
                          placeholder={"Enter ID"}
                          className={"bg-whiteBlack-100"}
                          disabled={isCustomerLoggedIn}
                          color={
                            errors.memeber_or_customer_id && touched.memeber_or_customer_id
                              ? "failure"
                              : undefined
                          }
                        />
                      </FormGroup>
                    ) : null}

                    {isCustomerLoggedIn ? (
                      <>
                        <div className={"flex flex-row items-center justify-start gap-2"}>
                          <ValidPromoCodeIcon />
                          <span className={"text-green-700 text12Semibold"}>
                            {isMobile
                              ? `Membership Applied - ${loggedInCustomerDetails?.member_code ?? ""}`
                              : `Verified`}
                          </span>
                        </div>
                      </>
                    ) : (
                      <Button
                        onClick={() => submitForm()}
                        className={`${isMobile ? "w-full" : ""}`}
                        color={"primary"}
                        disabled={isRequestingOtp}
                      >
                        {isRequestingOtp ? (
                          <Spinner size={"sm"} light />
                        ) : (
                          <span className={"text16Medium text-whiteBlack-100"}>Verify</span>
                        )}
                      </Button>
                    )}
                  </div>
                ) : null}
                {isCustomerLoggedIn && showMembershipDropdown ? (
                  <FormGroup
                    label={"Which Membership do you want to apply?"}
                    name={"membership_id"}
                    className={`flex-1 ${!isCustomerLoggedIn ? "mt-2" : "mt-1"}`}
                  >
                    <MonoSelect
                      inputId={"membership_id"}
                      {...getFieldProps("membership_id")}
                      className={"bg-whiteBlack-100"}
                      options={options ?? []}
                      onValueChange={async (membership) => {
                        if (membership) {
                          await onMemberShipSelect?.(membership?.value, "apply")
                        } else {
                          await onMemberShipSelect?.(membership?.value, "remove")
                        }
                      }}
                      placeholder={"Select Membership"}
                      isClearable
                    />
                  </FormGroup>
                ) : null}

                {showOtpVerificationModal ? (
                  <CustomerOtpVerificationModal
                    show={Boolean(showOtpVerificationModal)}
                    handleClose={() => setShowOtpVerificationModal(undefined)}
                    email={values.email}
                    isRedirectEnabled={false}
                    onSuccessCustomerLogin={onSuccessOtpVerification}
                    isIntegratedPage={isIntegratedPage}
                    initialSendOtpResponse={showOtpVerificationModal}
                  />
                ) : null}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  )
}

export default forwardRef(VerifyMembershipForm)
